import { endpoints } from '@/api/endpoints';
import { MasterCrud } from '@/components/MasterCrud';
import { Input, Select } from '@/components/ui/Input';
import { Pill } from '@/components/ui/Pill';

interface BranchRow {
  id: number;
  name: string;
  type?: string;
  city?: string;
  state?: string;
  state_code?: string;
  pincode?: string;
  is_default?: boolean;
  is_active?: boolean;
  can_bill?: boolean;
  gstin?: string;
}

interface BranchForm {
  name: string;
  type: 'store' | 'warehouse' | 'office';
  address_line1: string;
  city: string;
  state: string;
  state_code: string;
  pincode: string;
  gstin: string;
  can_bill: boolean;
  is_active: boolean;
}

export function BranchesPage() {
  return (
    <MasterCrud<BranchRow, BranchForm>
      title="Branches"
      description="Stores, warehouses and back offices — anywhere your stock lives."
      listUrl={endpoints.branches}
      itemUrl={endpoints.branch}
      queryKey="branches"
      newForm={() => ({
        name: '',
        type: 'store',
        address_line1: '',
        city: '',
        state: '',
        state_code: '',
        pincode: '',
        gstin: '',
        can_bill: true,
        is_active: true,
      })}
      toForm={(r) => ({
        name: r.name ?? '',
        type: (r.type as BranchForm['type']) ?? 'store',
        address_line1: '',
        city: r.city ?? '',
        state: r.state ?? '',
        state_code: r.state_code ?? '',
        pincode: r.pincode ?? '',
        gstin: r.gstin ?? '',
        can_bill: r.can_bill ?? true,
        is_active: r.is_active ?? true,
      })}
      toBody={(f) => ({
        name: f.name.trim(),
        type: f.type,
        address_line1: f.address_line1.trim() || undefined,
        city: f.city.trim() || undefined,
        state: f.state.trim() || undefined,
        state_code: f.state_code.trim() || undefined,
        pincode: f.pincode.trim() || undefined,
        gstin: f.gstin.trim() || undefined,
        can_bill: f.can_bill,
        is_active: f.is_active,
      })}
      columns={[
        {
          key: 'name',
          header: 'Branch',
          cell: (r) => (
            <div className="flex items-center gap-2">
              <span className="font-medium">{r.name}</span>
              {r.is_default && <Pill tone="brand">Default</Pill>}
              {!r.is_active && <Pill tone="neutral">Inactive</Pill>}
            </div>
          ),
        },
        {
          key: 'type',
          header: 'Type',
          hideOnMobile: true,
          cell: (r) => <span className="capitalize">{r.type ?? '—'}</span>,
        },
        {
          key: 'city',
          header: 'City',
          hideOnMobile: true,
          cell: (r) =>
            [r.city, r.state_code].filter(Boolean).join(', ') || '—',
        },
        {
          key: 'gstin',
          header: 'GSTIN',
          hideOnMobile: true,
          cell: (r) => (
            <span className="font-mono text-xs">{r.gstin ?? '—'}</span>
          ),
        },
        {
          key: 'bill',
          header: 'Bills?',
          cell: (r) =>
            r.can_bill ? (
              <Pill tone="success">Yes</Pill>
            ) : (
              <Pill tone="neutral">No</Pill>
            ),
        },
      ]}
      formFields={(value, setValue) => (
        <div className="space-y-3">
          <Input
            name="name"
            label="Branch name"
            autoFocus
            value={value.name}
            onChange={(e) => setValue({ ...value, name: e.target.value })}
          />
          <Select
            name="type"
            label="Type"
            value={value.type}
            onChange={(e) =>
              setValue({ ...value, type: e.target.value as BranchForm['type'] })
            }
          >
            <option value="store">Store</option>
            <option value="warehouse">Warehouse</option>
            <option value="office">Office</option>
          </Select>
          <Input
            name="address_line1"
            label="Address"
            value={value.address_line1}
            onChange={(e) =>
              setValue({ ...value, address_line1: e.target.value })
            }
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              name="city"
              label="City"
              value={value.city}
              onChange={(e) => setValue({ ...value, city: e.target.value })}
            />
            <Input
              name="pincode"
              label="Pincode"
              value={value.pincode}
              onChange={(e) => setValue({ ...value, pincode: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              name="state"
              label="State"
              value={value.state}
              onChange={(e) => setValue({ ...value, state: e.target.value })}
            />
            <Input
              name="state_code"
              label="State code"
              value={value.state_code}
              maxLength={2}
              onChange={(e) =>
                setValue({ ...value, state_code: e.target.value })
              }
            />
          </div>
          <Input
            name="gstin"
            label="GSTIN (if branch is GST-registered)"
            value={value.gstin}
            onChange={(e) => setValue({ ...value, gstin: e.target.value })}
          />
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={value.can_bill}
                onChange={(e) =>
                  setValue({ ...value, can_bill: e.target.checked })
                }
                className="h-4 w-4 accent-brand-600"
              />
              Bills can be raised here
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={value.is_active}
                onChange={(e) =>
                  setValue({ ...value, is_active: e.target.checked })
                }
                className="h-4 w-4 accent-brand-600"
              />
              Active
            </label>
          </div>
        </div>
      )}
    />
  );
}
